<template>
  <div
    :class="['course-item', { 'course-item--mini': mini }]"
    @click="emit('click')"
  >
    <div class="course-item__wrapper">
      <div class="course-item__img-wrapper">
        <img
          v-if="thumbnailDownloadUrl"
          class="course-item__img"
          :src="thumbnailDownloadUrl"
          :style="{ marginBottom: mini ? '0px' : '10px' }"
        />
        <div
          v-else
          class="course-item__img course-item__img--placeholder"
        ></div>
        <div class="course-item__crumb" v-if="themeName">{{ themeName }}</div>
        <div
          :class="[
            'course-item__crumb',
            'course-item__crumb--status',
            {
              'course-item__crumb--green': progressStatus.value === 'Finished',
            },
            {
              'course-item__crumb--blue':
                progressStatus.value === 'In progress',
            },
          ]"
          v-if="progressStatus && !mini"
        >
          {{ progressStatus.title }}
          <img
            v-if="progressStatus.value === 'In progress'"
            class="course-item__crumb-icon"
            src="/assets/icon/battery-icon.svg"
          />
        </div>
      </div>
      <div v-if="!mini" class="course-item__description">
        {{ course.description }}
      </div>
      <div v-if="!mini" class="course-item__meta">
        <div v-if="metaTheoryLessonsCount" class="course-item__meta-item">
          <ion-icon icon="/assets/icon/book-icon.svg" />
          {{ t('theoryLessons', { n: metaTheoryLessonsCount }) }}
        </div>
        <div v-if="metaPracticeLessonsCount" class="course-item__meta-item">
          <ion-icon icon="/assets/icon/pen-icon.svg" />
          {{ t('practicalExercises', { n: metaPracticeLessonsCount }) }}
        </div>
        <div v-if="course.timesPerDay" class="course-item__meta-item">
          <ion-icon icon="/assets/icon/alarm-icon.svg" />
          {{ course.timesPerDay }} {{ t('perDay') }}
        </div>
        <div v-if="metaFirstAidLessonsCount" class="course-item__meta-item">
          <ion-icon icon="/assets/icon/med-icon.svg" />
          {{ t('firstAidKitVideos', { n: metaFirstAidLessonsCount }) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { CourseI } from '@/types';
import { getFirebaseImagePath } from '@/services/methods';

import { IonIcon } from '@ionic/vue';
import api from '@/api';
import { useUserStore } from '@/stores/user';
import { useInternalization } from '@/composables/useInternalization';

const props = defineProps<{ course: CourseI; mini?: boolean }>();
const emit = defineEmits(['click']);
const thumbnailDownloadUrl = ref('');
const themeName = ref('');

const userStore = useUserStore();
const { t, locale } = useInternalization();

const metaTheoryLessonsCount = computed(
  () =>
    props.course.videoItems.filter((video) => video.type === 'theory').length
);
const metaPracticeLessonsCount = computed(
  () =>
    props.course.videoItems.filter((video) => video.type === 'practice').length
);
const metaFirstAidLessonsCount = computed(
  () =>
    props.course.videoItems.filter((video) => video.type === 'first aid').length
);

const progressStatus = computed(() => {
  const userCourse = userStore.user.therapyCourses?.find(
    (c) => c.id === props.course.id
  );
  if (userCourse) {
    return {
      title: userCourse.isFinished ? t('finished') : t('inProgress'),
      value: userCourse.isFinished ? 'Finished' : 'In progress',
    };
  }
  return null;
});

async function getThumbnailUrl() {
  thumbnailDownloadUrl.value = await getFirebaseImagePath(
    props.course.thumbnailUrl
  );
}
async function getTheme() {
  const theme = await api.themes.getById(props.course.themeId);
  const name = theme.translations[locale.value]?.name || theme.name;
  themeName.value = name || '';
}
getThumbnailUrl();
getTheme();
</script>

<style lang="scss" scoped>
.course-item {
  padding: 8px;
  background-color: #fff;
  border-radius: 20px;
  padding-bottom: 16px;

  &--mini {
    padding: 0;
    padding-bottom: 0;
    // .course-item__wrapper {
    // 	padding: 10px;
    // }
  }

  &__crumb {
    position: absolute;
    bottom: 10px;
    left: 10px;
    font-size: 0.65rem;
    background-color: #9ed9ce;
    border-radius: 20px;
    padding: 5px 10px;
    color: #172659;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &--status {
      bottom: 36px;
    }
    &--blue {
      background-color: #ac6bff;
      color: #fff;
    }
    &--green {
      background-color: #8fe29d;
    }

    &-icon {
      padding-left: 5px;
    }
  }

  &__meta {
    display: grid;
    gap: 8px;

    @media (max-width: 505px) {
      grid-template-columns: repeat(2, 1fr);
    }

    &-item {
      font-size: 0.85rem;
      color: #6288ff;
      display: flex;
      align-items: center;

      ion-icon {
        margin-right: 0.3em;
      }
    }
  }
  &__description {
    margin-bottom: 10px;
    margin-right: 20px;
    font-size: 0.9rem;
    font-weight: 400;
    line-height: 20px;
  }
  &__img {
    aspect-ratio: 16/9;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    margin-bottom: 10px;
    border-radius: 10px;
    overflow: hidden;

    &--placeholder {
      background-color: #bfe5fb;
    }
    &-wrapper {
      position: relative;
    }
  }
}
</style>
